import { Box, CircularProgress, InputAdornment, Typography, useTheme } from '@mui/material';
import {
  useCreateAgentPhoneNumber,
  useDeleteAgentPhoneNumber,
  useGetPhoneNumbers
} from 'apis/business/agent/deployPhone/hook';
import { useGetAgent, useUpdateAgent } from 'apis/business/agent/hook';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuRefreshCw } from 'react-icons/lu';
import { confirmDialog } from 'utils/helperFunctions';
import Grid from '@mui/material/Grid2';
import Spinner from 'components/Common/Spinner';
import CAflag from 'assets/CAflag.svg';
import USflag from 'assets/USflag.svg';
import INDflag from 'assets/INDflag.svg';
import Radio from 'components/Common/Radio';
import { useGetCurrentUser } from 'apis/user/hook';

export default function BuyNewNumberForm({ setSelectedOption }: any) {
  const theme = useTheme();
  const { data: agent, isLoading } = useGetAgent();
  const { data: currentUser } = useGetCurrentUser();
  const [showNumber, setShowNumber] = useState<'existing' | boolean | null>(null);
  const [numberType, setNumberType] = useState<'existing' | 'new' | null>(null);
  const [numberPurchased, setNumberPurchased] = useState<string | null>(null);

  const { mutateAsync: createAgentPhoneNumber, isPending } = useCreateAgentPhoneNumber();
  const { mutateAsync: updateAgent } = useUpdateAgent();
  const { mutateAsync: deleteAgentPhoneNumber } = useDeleteAgentPhoneNumber();

  const { control, watch, reset, handleSubmit } = useForm({
    values: {
      phone_number: {
        country_code: agent?.country_code || 'CA',
        phone_number: agent?.phone_number
      },
      agent: {
        availability: agent?.availability || ''
      }
    }
  });

  const { data: phoneNumbers } = useGetPhoneNumbers(
    watch('phone_number.country_code'),
    String(watch('phone_number.phone_number')),
    {
      queryKey: ['phoneNumbers', watch('phone_number.country_code'), numberType],
      enabled: Boolean(showNumber && numberType)
    },
    numberType!,
    showNumber
  );

  const onSubmit = async (number: any) => {
    setNumberPurchased(number);
    setNumberType(null);
    let payload = {
      phone_number: {
        phone_number: number,
        country_code: watch('phone_number.country_code'),
        existing: Boolean(numberType === 'existing'),
        imported: false
      }
    };
    const result = await confirmDialog({
      title: 'Are you sure?',
      text: 'Do you want to add this number to your Deploy Agent to Phone?',
      icon: 'warning',
      confirmButtonColor: theme.palette.primary.main
    });
    if (result.isConfirmed) createAgentPhoneNumber(payload);
  };

  const changeNumber = async () => {
    deleteAgentPhoneNumber();
    reset({
      phone_number: {
        phone_number: ''
      }
    });
  };

  const handleUpdateAgent = async (data: any) => {
    updateAgent(data);
  };
  const handleChangeMethod = () => {
    if (agent?.active_number) {
      deleteAgentPhoneNumber();
    } else {
      setSelectedOption(null);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6' marginBottom={2} fontSize='500'>
          Deploy Phone
        </Typography>
        <Button variant='text' startIcon={<LuRefreshCw />} color='primary' onClick={() => handleChangeMethod()}>
          Change Method
        </Button>
      </Box>
      <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1}>
        <Typography textAlign='center' variant='h6' fontSize='500'>
          Deploy Agent to Phone
        </Typography>
        <Typography color='secondary' variant='body2' textAlign='center' mt={1} mb={2}>
          This number will cost $2.00/mo, in addition to the charges for minutes spoken.
        </Typography>
        <Box display='flex' justifyContent='center' textAlign='center' alignItems='center' gap={2} mt={3}>
          <Box maxWidth={400} width='100%'>
            <Input
              sx={{ width: '100%' }}
              name='phone_number.phone_number'
              label={Boolean(agent?.phone_number) ? 'Business phone number' : 'Enter area code'}
              control={control}
              disabled={Boolean(agent?.phone_number)}
              defaultValue=''
              autoComplete='off'
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Select
                        control={control}
                        name='phone_number.country_code'
                        label=''
                        options={countries}
                        defaultValue='CA'
                        variant='standard'
                        disableUnderline
                        disabled={Boolean(agent?.phone_number)}
                        sx={{
                          minWidth: 25,
                          '.MuiSelect-select': {
                            paddingTop: 0,
                            paddingBottom: 0
                          }
                        }}
                        IconComponent={() => <></>}
                      />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Box>
          {Boolean(agent?.phone_number) ? (
            <Button sx={{ padding: '10px 24px', height: '40px' }} onClick={() => changeNumber()}>
              Change Number
            </Button>
          ) : (
            <>
              <Button
                sx={{ padding: '10px 24px', height: '40px' }}
                onClick={() => {
                  setShowNumber(true);
                  setNumberType('new');
                }}
              >
                Show Number
              </Button>
              {currentUser?.agency_user && (
                <Box mt={4}>
                  <Button
                    sx={{ padding: '10px 24px', height: '40px' }}
                    onClick={() => {
                      setShowNumber('existing');
                      setNumberType('existing');
                    }}
                  >
                    Show Existing Number
                  </Button>
                  <Typography variant='body2' color='text.secondary' fontSize={12}>
                    Visible to Agency Owner
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
        {!Boolean(agent?.phone_number) && (
          <Grid container columnSpacing={2} rowSpacing={3} mt={3} width={600} mx='auto'>
            {phoneNumbers?.numbers?.map((number, index) => (
              <Grid
                size={4}
                textAlign='center'
                sx={{ cursor: 'pointer', padding: '8px 24px' }}
                key={index}
                border='1px solid #777777'
                color='#777'
                borderRadius={10}
                onClick={() => onSubmit(number)}
              >
                {isPending && numberPurchased === number ? <CircularProgress color='primary' size={20} /> : number}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Box component='form' onSubmit={handleSubmit(handleUpdateAgent)}>
        <Box bgcolor={theme.palette.background.paper} padding={3} marginTop={3} borderRadius={1}>
          <Typography variant='h6' fontSize='500' marginBottom={2}>
            Phone Agent Availability
          </Typography>
          <Radio
            name='agent.availability'
            control={control}
            label=''
            options={[
              { label: 'Always', value: 'always' },
              { label: 'After Business Hours', value: 'after_business_hours' }
            ]}
          />
        </Box>
        <Button type='submit' sx={{ display: 'block', marginLeft: 'auto', marginTop: '24px' }}>
          Save
        </Button>
      </Box>
    </>
  );
}

const countries = [
  { value: 'US', label: <img alt='usa' src={USflag} width={37} height={37} /> },
  { value: 'CA', label: <img alt='canada' src={CAflag} width={37} height={37} /> },
  { value: 'IN', label: <img alt='india' src={INDflag} width={37} height={37} /> }
];
